import React from "react";
import { Link } from "react-router-dom";
import { Lock, Trophy } from "lucide-react";

/**
 * BadgeRail — shows the seven possible certifications (1 primary + 6 specialists)
 * as a visual row of earned/locked badges. Used on the Progress page and (compact
 * mode) inside the AppShell header.
 *
 * Props:
 *  - certs: [{ slug, name, emoji, tier, bonus_slug }]
 *  - passedSlugs: Set<string>  (module slugs whose quiz has been passed)
 *  - primaryEarned: boolean
 *  - compact: boolean  (renders a smaller pill for the header)
 */
export default function BadgeRail({ certs = [], passedSlugs = new Set(), primaryEarned = false, compact = false }) {
  const primary = certs.find((c) => c.tier === "primary");
  const specialists = certs.filter((c) => c.tier === "specialist");
  const items = primary ? [primary, ...specialists] : specialists;

  const isEarned = (c) =>
    c.tier === "primary" ? primaryEarned : passedSlugs.has(c.bonus_slug);

  const earnedCount = items.filter(isEarned).length;

  if (compact) {
    return (
      <Link
        to="/progress"
        className="hidden md:inline-flex items-center gap-2 rounded-full border border-shell-border bg-white pr-3 pl-1 py-1 hover:border-primary transition-colors"
        data-testid="header-badge-rail"
        title={`${earnedCount} of ${items.length} certifications earned`}
      >
        <span className="flex -space-x-1.5">
          {items.slice(0, 4).map((c) => {
            const earned = isEarned(c);
            return (
              <span
                key={c.slug}
                className={[
                  "w-6 h-6 rounded-full grid place-items-center text-[13px] border-2 border-white shadow-sm",
                  earned ? "bg-shell-accent" : "bg-muted opacity-60",
                ].join(" ")}
              >
                {earned ? c.emoji : ""}
              </span>
            );
          })}
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-widest text-foreground/80">
          {earnedCount}/{items.length}
        </span>
      </Link>
    );
  }

  return (
    <section className="mt-8" data-testid="badge-rail">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-shell-accent font-semibold flex items-center gap-1">
            <Trophy className="w-3 h-3" /> Your badge wall
          </p>
          <h2 className="font-display text-2xl mt-1">
            {earnedCount === 0
              ? "Earn your first badge"
              : `${earnedCount} / ${items.length} badges earned`}
          </h2>
        </div>
        <p className="text-xs text-muted-foreground max-w-xs">
          Each badge lights up the moment you pass its module quiz. Tap a badge to open the module.
        </p>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {items.map((c) => {
          const earned = isEarned(c);
          const target = c.tier === "primary"
            ? "/progress"
            : c.bonus_slug ? `/modules/${c.bonus_slug}` : "/progress";
          return (
            <Link
              key={c.slug}
              to={target}
              data-testid={`badge-tile-${c.slug}`}
              className={[
                "surface p-3 pt-6 flex flex-col items-center text-center group relative overflow-hidden transition-transform min-w-0",
                earned ? "hover:-translate-y-0.5" : "opacity-70 hover:opacity-100",
              ].join(" ")}
            >
              <div
                className={[
                  "w-12 h-12 rounded-full grid place-items-center text-2xl transition-colors",
                  earned
                    ? "bg-gradient-to-br from-pink-300 via-shell-accent to-primary text-white shadow-md"
                    : "bg-muted text-muted-foreground",
                ].join(" ")}
              >
                {earned ? c.emoji : <Lock className="w-4 h-4" />}
              </div>
              <p className={[
                "mt-2 text-[10px] uppercase font-semibold leading-tight break-words hyphens-auto tracking-[0.04em] lg:tracking-widest px-1",
                earned ? "text-foreground" : "text-muted-foreground",
              ].join(" ")}>
                {c.name.replace("Certified ", "").replace(" Specialist™", "").replace("™", "")}
              </p>
              {earned && (
                <span className="absolute top-1.5 left-1/2 -translate-x-1/2 text-[8px] uppercase tracking-widest bg-green-100 text-green-700 rounded px-1.5 py-0.5 font-bold whitespace-nowrap">
                  Earned
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}
